import React, { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  Typography
} from "@material-ui/core";
import { AsyncDropdown } from "src/apps/commons/dropdowns";
import { listAllRemyndBrands } from "src/apps/commons/dropdowns/api";
import { toDropdownOrNull } from "src/utils";
import { IRemyndBrandBrief } from "../../remyndBrand/types";

const useStyles = makeStyles(_ => ({
  container: {
    "& > *": {
      padding: "12px 6px"
    }
  }
}));

interface Props {
  label: string;
  open: boolean;
  defaultValue?: IRemyndBrandBrief;
  existingValues?: IRemyndBrandBrief[];
  handleCloseModal: () => void;
  handleSubmitModal: (value: IRemyndBrandBrief) => void;
}

const schema = yup.object().shape({
  selected: yup.object().typeError("กรุณาเลือกแบรนด์")
});

type FormData = {
  selected: IDropdown<IRemyndBrandBrief> | null;
};

const BrandDropdownModal: React.FC<Props> = props => {
  const classes = useStyles();
  const {
    open,
    label,
    defaultValue,
    handleCloseModal,
    handleSubmitModal,
    existingValues
  } = props;

  const defaultFormData = useMemo<FormData>(
    () => ({
      selected: toDropdownOrNull(defaultValue)
    }),
    [defaultValue]
  );
  const excludedFromDropdown = useMemo(
    () =>
      existingValues?.filter(b => b.id !== defaultValue?.id)?.map(b => b.id),
    [existingValues, defaultValue]
  );

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: defaultFormData,
    resolver: yupResolver(schema)
  });

  const handleTransformAndSubmit = (form: FormData) => {
    if (form.selected) handleSubmitModal(form.selected.meta);
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="sm"
      fullWidth
      aria-labelledby="form-dialog-title"
    >
      <form onSubmit={handleSubmit(handleTransformAndSubmit)}>
        <DialogTitle id="form-dialog-title" disableTypography>
          <Typography variant="h3">
            {!defaultValue ? "เพิ่ม " : "แก้ไข "}
            {label}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container className={classes.container}>
            <Grid item xs={12}>
              <Controller
                name="selected"
                control={control}
                defaultValue={defaultFormData.selected ?? null}
                render={({ field: { onChange, value } }) => (
                  <AsyncDropdown
                    label="แบรนด์"
                    fetchDropdown={listAllRemyndBrands}
                    paginate={{
                      page: 1,
                      pageSize: 2000,
                      order: {
                        optionKey: "NAME_TH",
                        desc: false
                      }
                    }}
                    excludedDropdownValues={excludedFromDropdown}
                    onChange={onChange}
                    value={value}
                    error={Boolean(errors.selected)}
                    helperText={errors.selected?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Cancel
          </Button>
          {!defaultValue ? (
            <Button
              key="addBtn"
              variant="contained"
              color="primary"
              type="submit"
            >
              Add
            </Button>
          ) : (
            <Button
              key="updateBtn"
              variant="contained"
              color="primary"
              type="submit"
            >
              Update
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BrandDropdownModal;
